import {classList, cssVars, dataset, listen} from 'nuffjs';
import {getScrollTop} from '../utils/get-scroll';

/**
 * Configuration values
 *
 * @property {string} overContentClass=overContent class set when the header is overlapping content,
 * eventually used to set a solid or partially transparent background
 * @property {string} collapsedClass=collapsed class set to indicate the header should disappear
 * (typically floats up above the viewport top)
 * @property {string} floatingProp=floating CSS variable that can be used to disable the effect in
 * certain media queries. It takes precedence over the data-attribute
 *
 * @memberof CollapsableHeader
 */
const conf = {
	overContentClass: 'overContent',
	collapsedClass: 'collapsed',
	floatingProp: 'floating',
};

/**
 * @classdesc Header component that helps to implement the floating behavior typical of mobile
 * platforms (hide when scrolling down, show when scrolling up). It justs set css classes in certain
 * condition, the actual effect is demanded to CSS.
 *
 * @class CollapsableHeader
 * @extends {HTMLElement}
 * @version 1.0.0
 * @path
 *
 */
class CollapsableHeader extends HTMLElement {
	static conf = conf;

	/**
	 * @property {boolean} floating=true indicates if the behavior should depend on the scroll
	 * direction. When false, the collapsed class is set after the specified ratio regardless of the
	 * scroll direction
	 * @property {number} floatingCollapsingThresholdRatio=1 ratio of the vertical size to be
	 * considered as a trigger point, when the floating is enabled. 1 = the header size, 2 = twice
	 * the header size, and so on
	 * @property {number} nonFloatingCollapsingThresholdRatio=2 ratio of the vertical size to be
	 * considered as a trigger point, when the floating is disabled. 1 = the header size, 2 = twice
	 * the header size, and so on
	 *
	 * @static
	 * @memberof CollapsableHeader
	 */
	static defaultOpts = {
		floating: true,
		floatingCollapsingThresholdRatio: 1,
		nonFloatingCollapsingThresholdRatio: 2,
	};

	size = 0;
	previousScrollPos = -1;
	overContent = false;
	collapsed = false;
	listeners = [];

	/**
	 * Initialization
	 *
	 * @memberof CollapsableHeader
	 */
	firstConnectedCallback() {
		this.opts = dataset(this).get(this.constructor.defaultOpts);
		this.floating = this.opts.floating;
		this.initListeners();
		this.updateSize();
	}

	/**
	 * Listeners are attached on the window, so we attach and detach them together with the
	 * component, in the (remote) case the element is going to be attached / detached from the DOM
	 *
	 * @memberof CollapsableHeader
	 */
	connectedCallback() {
		this.initListeners();
	}

	/**
	 * Listeners are attached to the window, so we set and unset them together with the
	 * component, in the (remote) case the element is going to be attached / detached from the DOM
	 *
	 * @memberof CollapsableHeader
	 */
	disconnectedCallback() {
		this.clearListeners();
	}

	/**
	 * Set the listeners. Listeners are throttled
	 *
	 * @memberof CollapsableHeader
	 * @protected
	 * @ignore
	 */
	initListeners() {
		this.listeners = [
			listen(window, 'resize', this.onResize.bind(this), {throttle: true}),
			listen(window, 'scroll', this.onScroll.bind(this), {throttle: true}),
		];
	}

	/**
	 * Unset the listeners
	 *
	 * @memberof CollapsableHeader
	 * @protected
	 * @ignore
	 */
	clearListeners() {
		for (const listener of this.listeners) {
			if (listener) {
				listener.unlisten();
			}
		}
		this.listeners = [];
	}

	/**
	 * Handle resize event
	 *
	 * @param {Event} event resize event
	 * @memberof CollapsableHeader
	 */
	onResize(event) {
		this.updateSize();
	}

	/**
	 * Handle scroll event
	 *
	 * @param {Event} event the scroll event
	 * @memberof CollapsableHeader
	 */
	onScroll(event) {
		this.updateState();
	}

	/**
	 * Recalculate sizing and check the css variable on resize
	 *
	 * @memberof CollapsableHeader
	 */
	updateSize() {
		this.size = this.getBoundingClientRect().height;
		if (this.size === 0) {
			this.size = this.scrollHeight;
		}
		// if the value is defined by css var it has precedence over the value set by attribute
		const styleFloating = cssVars(this).get(conf.floatingProp);
		if (styleFloating === 1) {
			this.floating = true;
		} else if (styleFloating === 0) {
			this.floating = false;
		}
		this.updateState();
	}

	/**
	 * Recalculate the state on scroll
	 *
	 * @memberof CollapsableHeader
	 */
	updateState() {
		const scrollPos = getScrollTop();
		if (scrollPos !== this.previousScrollPos) {
			const opts = this.opts;
			const direction = scrollPos >= this.previousScrollPos ? 1 : -1;
			const threshold =
				this.size *
				(this.floating
					? opts.floatingCollapsingThresholdRatio
					: opts.nonFloatingCollapsingThresholdRatio);
			const newOverContent = this.floating && scrollPos > threshold;
			const newCollapsed = scrollPos > threshold && (direction === 1 || !this.floating);
			const cl = classList(this);
			if (newOverContent !== this.overContent) {
				cl.toggle(conf.overContentClass, newOverContent);
				this.overContent = newOverContent;
			}
			if (newCollapsed !== this.collapsed) {
				cl.toggle(conf.collapsedClass, newCollapsed);
				this.collapsed = newCollapsed;
			}
			this.previousScrollPos = scrollPos;
		}
	}
}

export {CollapsableHeader};
